import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Message, ConversationStep, ConversationState } from '../types'
import type { ClientConfig } from '../types'
import { initializeConversation, processUserInput, createUserMessage, createBotMessage } from '../engine/stateMachine'
import { generateBotName } from '../utils/randomName'
import { generateUUID } from '../utils/uuid'
import { buildLeadData } from '../utils/leadBuilder'
import { submitLead, getMessageResponse } from '../services/apiClient'

export interface ChatState {
  // UI state
  isOpen: boolean
  unreadCount: number

  // Conversation state
  conversationId: string
  messages: Message[]
  currentStep: ConversationStep
  botName: string
  calendarPushCount: number
  startedAt: Date | null

  // Lead data being collected
  leadData: Record<string, unknown>

  // Client configuration
  clientConfig: ClientConfig | null

  // Actions
  toggleChat: () => void
  openChat: () => void
  closeChat: () => void
  addMessage: (message: Message) => void
  setStep: (step: ConversationStep) => void
  updateLeadData: (data: Record<string, unknown>) => void
  incrementCalendarPush: () => void
  setClientConfig: (config: ClientConfig) => void
  setBotName: (name: string) => void
  markMessagesAsRead: () => void
  initConversation: (clientId: string) => void
  handleUserInput: (input: string) => void
  reset: () => void
}

const initialState = {
  isOpen: false,
  unreadCount: 0,
  conversationId: '',
  messages: [],
  currentStep: 'welcome' as ConversationStep,
  botName: '',
  calendarPushCount: 0,
  startedAt: null,
  leadData: {},
  clientConfig: null,
}

export const useChatStore = create<ChatState>()(
  persist(
    (set, get) => ({
      ...initialState,

      toggleChat: () => {
        const { isOpen } = get()
        set({ isOpen: !isOpen })
        if (!isOpen) {
          // Mark messages as read when opening
          set({ unreadCount: 0 })
        }
      },

      openChat: () => {
        set({ isOpen: true, unreadCount: 0 })
      },

      closeChat: () => {
        set({ isOpen: false })
      },

      addMessage: (message: Message) => {
        const { messages, isOpen } = get()
        set({
          messages: [...messages, message],
          unreadCount: !isOpen && message.type === 'bot' ? get().unreadCount + 1 : get().unreadCount,
        })
      },

      setStep: (step: ConversationStep) => {
        set({ currentStep: step })
      },

      updateLeadData: (data: Record<string, unknown>) => {
        const { leadData } = get()
        set({ leadData: { ...leadData, ...data } })
      },

      incrementCalendarPush: () => {
        set({ calendarPushCount: get().calendarPushCount + 1 })
      },

      setClientConfig: (config: ClientConfig) => {
        set({ clientConfig: config })
      },

      setBotName: (name: string) => {
        set({ botName: name })
      },

      markMessagesAsRead: () => {
        set({ unreadCount: 0 })
      },

      initConversation: (clientId: string) => {
        const state = get()
        // Generate bot name using conversationId as seed for consistency
        const tempConversationId = generateUUID()
        const botName = generateBotName(tempConversationId)

        // Initialize conversation using state machine (pass conversationMode if available)
        const conversationMode = state.clientConfig?.conversationMode
        const conversationState = initializeConversation(clientId, botName, conversationMode)

        set({
          conversationId: conversationState.conversationId,
          messages: conversationState.messages,
          currentStep: conversationState.step as ConversationStep,
          botName: conversationState.botName,
          calendarPushCount: conversationState.calendarPushCount,
          startedAt: conversationState.startedAt,
          leadData: conversationState.leadData,
        })
      },

      handleUserInput: async (input: string) => {
        const state = get()

        // Create user message and add to chat
        const userMessage = createUserMessage(input)
        set({
          messages: [...state.messages, userMessage],
        })

        // Build current conversation state for state machine
        const currentConversationState: ConversationState = {
          conversationId: state.conversationId,
          step: state.currentStep,
          leadData: state.leadData,
          botName: state.botName,
          calendarPushCount: state.calendarPushCount,
          messages: state.messages,
          startedAt: state.startedAt || new Date(),
        }

        // Special handling for freeform or question steps - call Claude AI
        if (state.currentStep === 'question_input' || state.currentStep === 'freeform_welcome') {
          // Store the question in lead data
          const updatedLeadData = { ...state.leadData, userQuestion: input }

          // Show typing indicator
          const typingMessage = createBotMessage('...', currentConversationState)
          set({
            messages: [...state.messages, userMessage, typingMessage],
            leadData: updatedLeadData,
          })

          // Call Claude API for intelligent response
          try {
            const leadType = state.leadData.type as 'buyer' | 'seller' | 'question' | undefined
            const leadName = state.leadData.name ? String(state.leadData.name) : undefined
            // Always use clientConfig.clientId (current) over leadData.clientId (potentially stale from localStorage)
            const clientId = String(state.clientConfig?.clientId || state.leadData.clientId || 'unknown')

            const response = await getMessageResponse({
              clientId,
              userQuestion: input,
              leadContext: leadType || leadName ? {
                ...(leadName ? { name: leadName } : {}),
                ...(leadType ? { type: leadType } : {}),
              } : undefined,
            })

            // Remove typing indicator and add AI response
            const aiResponseMessage = createBotMessage(
              response.response,
              currentConversationState
            )

            // For freeform mode (storefront), stay in freeform - no deflection buttons
            if (state.currentStep === 'freeform_welcome') {
              set({
                messages: [
                  ...state.messages,
                  userMessage,
                  aiResponseMessage,
                ],
                currentStep: 'freeform_welcome', // Stay in freeform mode
                leadData: updatedLeadData,
                unreadCount: !state.isOpen ? state.unreadCount + 1 : state.unreadCount,
              })
            } else {
              // For question_input, add deflection buttons after AI response
              const agentName = state.clientConfig?.agentName || 'them'
              const deflectionMessage = createBotMessage(
                `Want to grab a quick time on ${agentName}'s calendar?`,
                currentConversationState,
                [
                  { label: '📅 Yeah, let\'s book it', value: 'book_it', emoji: '📅' },
                  { label: 'Just get my info for now', value: 'get_info' },
                ]
              )

              set({
                messages: [
                  ...state.messages,
                  userMessage,
                  aiResponseMessage,
                  deflectionMessage,
                ],
                currentStep: 'question_deflect',
                leadData: updatedLeadData,
                unreadCount: !state.isOpen ? state.unreadCount + 2 : state.unreadCount,
              })
            }

          } catch (error) {
            console.error('Failed to get AI response:', error)

            // Fallback to generic deflection on error
            const agentName = state.clientConfig?.agentName || 'them'
            const fallbackMessage = createBotMessage(
              `That's a great question! Honestly, ${agentName} can answer that way better than me. Want to grab a quick time on their calendar?`,
              currentConversationState,
              [
                { label: '📅 Yeah, let\'s book it', value: 'book_it', emoji: '📅' },
                { label: 'Just get my info for now', value: 'get_info' },
              ]
            )

            set({
              messages: [...state.messages, userMessage, fallbackMessage],
              currentStep: 'question_deflect',
              leadData: updatedLeadData,
              unreadCount: !state.isOpen ? state.unreadCount + 1 : state.unreadCount,
            })
          }

          return // Exit early - we've handled this step specially
        }

        // Process input through state machine (for all other steps)
        const result = processUserInput(
          state.currentStep,
          input,
          currentConversationState
        )

        // Update state with result
        const updatedLeadData = { ...state.leadData, ...result.updatedLeadData }
        set({
          currentStep: result.nextStep,
          leadData: updatedLeadData,
          messages: [...state.messages, userMessage, result.botMessage],
          unreadCount: !state.isOpen ? state.unreadCount + 1 : state.unreadCount,
        })

        // If conversation is complete, submit lead to API
        if (result.nextStep === 'thank_you') {
          const leadData = buildLeadData({
            ...updatedLeadData,
            conversationId: state.conversationId,
            botName: state.botName,
            clientId: updatedLeadData.clientId as string,
          })

          if (leadData) {
            // Submit lead asynchronously (don't block UI)
            submitLead(leadData)
              .then(() => {
                console.log('Lead submitted successfully:', leadData.leadId)
              })
              .catch((error) => {
                console.error('Failed to submit lead:', error)
                // Could show user error message here if needed
              })
          } else {
            console.error('Failed to build lead data - missing required fields')
          }
        }
      },

      reset: () => {
        const { clientConfig } = get()
        set({ ...initialState, clientConfig })
      },
    }),
    {
      name: 'hellabots-chat-storage',
      partialize: (state) => {
        // Don't persist Calendly steps - if user refreshes during Calendly, move to thank_you
        let stepToSave = state.currentStep
        if (
          stepToSave === 'buyer_show_calendly' ||
          stepToSave === 'seller_show_calendly'
        ) {
          stepToSave = 'thank_you'
        }

        return {
          // Only persist conversation data, not UI state
          conversationId: state.conversationId,
          messages: state.messages,
          currentStep: stepToSave,
          botName: state.botName,
          calendarPushCount: state.calendarPushCount,
          startedAt: state.startedAt,
          leadData: state.leadData,
          clientConfig: state.clientConfig,
        }
      },
    }
  )
)
